// Vendors
import { sessionUtils, cookies } from '@openbank/cf-ui-architecture-core';
import { StorageLocalEnum } from '@openbank/ziniaweb-ui-core-models';
// Constants
import {
  CUSTOMER_ALREADY_LOGGED_ACTION,
  TOKEN_STATUS_CHECK_1_ACTION,
  TOKEN_STATUS_CHECK_2_ACTION,
  TOKEN_STATUS_CHECK_3_ACTION,
  TOKEN_REFRESH_FAILED_CUSTOM_ACTION,
  TOKEN_REFRESHED_CUSTOM_ACTION,
} from './constants/token-log.handlers.constants';
import {
  CALLBACK_URL_QUERY_PARAM,
  LANGUAGE_QUERY_PARAM,
  TOKEN_QUERY_PARAM,
} from './constants/token.handlers.constants';
import { ONBOARDING_INIT_LOCAL_STORAGE } from 'constants/app.constants';
import { REFRESH_TOKEN_PROPS } from 'constants/app.constants';
// Enumerations
import { AppErrorEnumeration } from 'enumerations/app-error.enumeration';
import { AppInformationNeedEnumeration } from 'enumerations/app-information-need.enumeration';
// Event Handlers
import {
  translationsHasBeenLoaded,
  mustBeRedirectedAndNoLogged,
  getMsUntilRefresh,
} from './utils/token.handlers.utils';
// Types
import {
  TokenBuilderHandlersType,
  TokenMethodsHandlersType,
  TokenSaveAuthDataAndCleanUrlType,
} from './types/token.handlers.type';
// Utilities
import { fetchCustomerAuthorizationDataResolver } from '../resolvers/token.resolver';
import { getAppDomain } from 'utils/domain.utils';

let TIMEOUT;

const updateTokenInformationFlag =
  () =>
  (prevFlags: AppInformationNeedEnumeration[]): AppInformationNeedEnumeration[] => [
    ...prevFlags,
    AppInformationNeedEnumeration.AUTHORIZATION_DATA,
  ];

const removeTokenQueryParamFromUrl = (): void => {
  const url = new URL(window.location.href);

  url.searchParams.delete(TOKEN_QUERY_PARAM);
  window.history.replaceState({}, '', url.toString());
};

const saveOnboardingInitTimestamp = (): void => {
  if (!sessionStorage.getItem(ONBOARDING_INIT_LOCAL_STORAGE)) {
    sessionStorage.setItem(ONBOARDING_INIT_LOCAL_STORAGE, new Date().toString());
  }
};

const saveAuthDataAndCleanUrl = ({ response }: TokenSaveAuthDataAndCleanUrlType): void => {
  removeTokenQueryParamFromUrl();
  sessionUtils.saveSessionData({ ...response, domain: getAppDomain() });
  saveOnboardingInitTimestamp();
};

const runTokenStatusSanityChecks = async (
  handleSendCustomActionEvent: TokenBuilderHandlersType['handleSendCustomActionEvent']
): Promise<void> => {
  // Sanity check: confirmamos que el token viaja en las cabeceras antes de continuar
  await handleSendCustomActionEvent(TOKEN_STATUS_CHECK_1_ACTION);
  await handleSendCustomActionEvent(TOKEN_STATUS_CHECK_2_ACTION);
  await handleSendCustomActionEvent(TOKEN_STATUS_CHECK_3_ACTION);
};

const scheduleTokenRefresh = ({
  handleSendCustomActionEvent,
  informationFlags,
  urlSearchParams,
  setInformationFlags,
  setError,
}: TokenBuilderHandlersType): (() => void) => {
  const msUntilRefresh = getMsUntilRefresh();

  if (msUntilRefresh === null) {
    return () => undefined;
  }

  TIMEOUT = setTimeout(() => {
    performTokenRefresh({
      handleSendCustomActionEvent,
      informationFlags,
      urlSearchParams,
      setInformationFlags,
      setError,
    });
  }, msUntilRefresh);

  return () => {
    if (TIMEOUT) {
      clearTimeout(TIMEOUT);
    }
  };
};

const performTokenRefresh = async ({
  handleSendCustomActionEvent,
  informationFlags,
  urlSearchParams,
  setInformationFlags,
  setError,
}: TokenBuilderHandlersType): Promise<boolean> => {
  const response = await sessionUtils.refreshToken(REFRESH_TOKEN_PROPS);
  if (response) {
    await runTokenStatusSanityChecks(handleSendCustomActionEvent);
    await handleSendCustomActionEvent(TOKEN_REFRESHED_CUSTOM_ACTION);
    // Reprogramamos el siguiente refresco segun la nueva expiracion de sesion
    scheduleTokenRefresh({
      handleSendCustomActionEvent,
      informationFlags,
      urlSearchParams,
      setInformationFlags,
      setError,
    });
  } else {
    await handleSendCustomActionEvent(TOKEN_REFRESH_FAILED_CUSTOM_ACTION);
    setError(AppErrorEnumeration.AUTH_WEALTH_REFRESH_TOKEN_FAILED);
  }

  return Boolean(response);
};

const fetchCustomerAuthorizationDataHandler = async ({
  handleSendCustomActionEvent,
  informationFlags,
  urlSearchParams,
  setInformationFlags,
  setError,
}: TokenBuilderHandlersType): Promise<void> => {
  const token = urlSearchParams.get(TOKEN_QUERY_PARAM) || '';

  if (translationsHasBeenLoaded(informationFlags)) {
    if (mustBeRedirectedAndNoLogged()) {
      setError(AppErrorEnumeration.NO_REDIRECTED_FORCED_AND_STILL_STAY);
      // Significa que hemos forzado la redireccion y aun asi seguimos aqui
    } else {
      if (token) {
        sessionUtils.clearSessionData(getAppDomain());
        // Entendemos que viene de Amazon con token y puede que este logado
        if (!sessionUtils.isUserLogged()) {
          // Hemos logrado borrar auth data
          const callbackUrl = urlSearchParams.get(CALLBACK_URL_QUERY_PARAM) || '',
            language = urlSearchParams.get(LANGUAGE_QUERY_PARAM) || '';
          // Recuperamos auth data del one token
          const [response] = await fetchCustomerAuthorizationDataResolver(token);

          if (response) {
            saveAuthDataAndCleanUrl({ callbackUrl, language, response });
            // No llamamos a nada mas hasta terminar los 3 /log de sanity check
            await runTokenStatusSanityChecks(handleSendCustomActionEvent);
            setInformationFlags(updateTokenInformationFlag());
          } else {
            sessionUtils.clearSessionData(getAppDomain());
            setError(AppErrorEnumeration.NO_AUTH_DATA_FROM_QUERY_TOKEN);
          }
        } else {
          setError(AppErrorEnumeration.AUTH_DATA_CLEAN_FAILED);
        }
      } else if (sessionUtils.isUserLogged()) {
        // Si esta logado dejamos que siga
        // Refrescamos el token para actualizar la fecha de expiracion
        await performTokenRefresh({
          handleSendCustomActionEvent,
          informationFlags,
          urlSearchParams,
          setInformationFlags,
          setError,
        });
        await handleSendCustomActionEvent(CUSTOMER_ALREADY_LOGGED_ACTION);
        setInformationFlags(updateTokenInformationFlag());
      } else {
        // Si no hay token ni estamos logados damos error
        sessionUtils.clearSessionData(getAppDomain());
        setError(AppErrorEnumeration.NO_LOGGED_OR_QUERY_TOKEN);
      }
    }
  }
};

const TokenHandlers = (props: TokenBuilderHandlersType): TokenMethodsHandlersType => ({
  handleFetchCustomerAuthorizationData: () => fetchCustomerAuthorizationDataHandler(props),
});

export { TokenHandlers };
